/**
 * Support Hub - Main support and resources interface
 */

import React, { useState } from 'react'
import { useInspectorRef } from '../../../hooks/useInspectorRef'
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Stack,
  Chip
} from '@mui/material'
import {
  Help as HelpIcon,
  Book as BookIcon,
  PlayCircle as VideoIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  QuestionAnswer as FAQIcon,
  BugReport as BugIcon,
  Lightbulb as TipIcon,
  Article as ArticleIcon,
  Support as SupportIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon
} from '@mui/icons-material'
import Guides from './Guides'
import SystemStatus from './SystemStatus'
import ContactForm from './ContactForm'
import FAQ from './FAQ'

const SupportHub: React.FC = () => {
  const { fpCode: fp_code } = useInspectorRef()
  const [activeSection, setActiveSection] = useState<'overview' | 'guides' | 'status' | 'contact' | 'faq'>('overview')

  const quickLinks = [
    {
      title: 'Getting Started Guide',
      description: 'Learn the basics of using the Inspector tool',
      icon: <BookIcon />,
      action: () => setActiveSection('guides')
    },
    {
      title: 'System Status',
      description: 'Check current system health and connectivity',
      icon: <CheckIcon />,
      action: () => setActiveSection('status')
    },
    {
      title: 'Contact Support',
      description: 'Submit a support ticket or get in touch',
      icon: <EmailIcon />,
      action: () => setActiveSection('contact')
    },
    {
      title: 'FAQ',
      description: 'Find answers to common questions',
      icon: <FAQIcon />,
      action: () => setActiveSection('faq')
    }
  ]

  const troubleshootingSteps = [
    {
      icon: <CheckIcon color="success" />,
      title: 'Check System Status',
      description: 'Verify all systems are connected and syncing properly'
    },
    {
      icon: <WarningIcon color="warning" />,
      title: 'Review Exceptions',
      description: 'Look for any data exceptions that need attention'
    },
    {
      icon: <BugIcon color="error" />,
      title: 'Report Issues',
      description: 'Submit detailed issue reports for investigation'
    },
    {
      icon: <TipIcon color="info" />,
      title: 'Best Practices',
      description: 'Follow our recommended best practices guide'
    }
  ]

  const supportContacts = [
    {
      type: 'Email',
      value: 'support@poolwerx.com.au',
      icon: <EmailIcon />
    },
    {
      type: 'Phone',
      value: '1300 POOLWERX',
      icon: <PhoneIcon />
    },
    {
      type: 'Hours',
      value: 'Mon-Fri 9am-5pm AEST',
      icon: <SupportIcon />
    }
  ]

  if (activeSection === 'guides') {
    return (
      <Box sx={{ p: 3 }}>
        <Button onClick={() => setActiveSection('overview')} sx={{ mb: 2 }}>
          ← Back to Support Hub
        </Button>
        <Guides />
      </Box>
    )
  }

  if (activeSection === 'status') {
    return (
      <Box sx={{ p: 3 }}>
        <Button onClick={() => setActiveSection('overview')} sx={{ mb: 2 }}>
          ← Back to Support Hub
        </Button>
        <SystemStatus fpCode={fp_code!} />
      </Box>
    )
  }

  if (activeSection === 'contact') {
    return (
      <Box sx={{ p: 3 }}>
        <Button onClick={() => setActiveSection('overview')} sx={{ mb: 2 }}>
          ← Back to Support Hub
        </Button>
        <ContactForm fpCode={fp_code!} />
      </Box>
    )
  }

  if (activeSection === 'faq') {
    return (
      <Box sx={{ p: 3 }}>
        <Button onClick={() => setActiveSection('overview')} sx={{ mb: 2 }}>
          ← Back to Support Hub
        </Button>
        <FAQ />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Support & Resources
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Get help, access documentation, and contact support
        </Typography>
      </Box>

      {/* Quick Links */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickLinks.map((link, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 3 } }} onClick={link.action}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {link.icon}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {link.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {link.description}
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small">Learn More →</Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Troubleshooting Guide */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Troubleshooting Guide
            </Typography>
            <List>
              {troubleshootingSteps.map((step, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemIcon>{step.icon}</ListItemIcon>
                    <ListItemText
                      primary={step.title}
                      secondary={step.description}
                    />
                  </ListItem>
                  {index < troubleshootingSteps.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Contact Support
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              For urgent issues, please call during business hours
            </Alert>
            <List>
              {supportContacts.map((contact, index) => (
                <ListItem key={index}>
                  <ListItemIcon>{contact.icon}</ListItemIcon>
                  <ListItemText
                    primary={contact.type}
                    secondary={contact.value}
                  />
                </ListItem>
              ))}
            </List>
            <Button 
              variant="contained" 
              fullWidth 
              sx={{ mt: 2 }}
              onClick={() => setActiveSection('contact')}
            >
              Submit Support Ticket
            </Button>
          </Paper>
        </Grid>

        {/* Resources */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Popular Resources
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <ArticleIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={500}>
                        User Manual
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Complete guide to all Inspector features
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <VideoIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={500}>
                        Video Tutorials
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Step-by-step video guides
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <TipIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={500}>
                        Best Practices
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Tips for optimal system usage
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default SupportHub